import i18n from "@/i18n";
/**
 * FirstScheduleSetupSheet — Prompt 12L-1B first-use Workforce
 * schedule setup UI.
 *
 * Presentational Sheet. The manager selects the Monday on which
 * this location's first 3-week Workforce schedule should start.
 * Non-Monday calendar days are not selectable. No auto-snap. No
 * default date. The primary "Create schedule" button remains
 * disabled until a valid Monday is picked.
 *
 * This component performs NO HTTP requests. It emits
 * `onSubmit(anchorYmd)` to the parent (WorkforceSchedule) which
 * owns the POST + canonical reload flow (§4 / §11).
 *
 * Manager-facing language only — no anchor / config / cadence
 * terminology (§2 / §19).
 */
import { useState } from "react";
import {
    Sheet, SheetContent, SheetHeader, SheetTitle,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";


// Local YYYY-MM-DD from a JS Date, using ONLY local calendar
// fields (never a UTC-based conversion) so a Monday chosen in the
// browser's local timezone serializes as that same calendar day
// regardless of UTC offset (§9).
function localYMD(date) {
    if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
}

// Parse a YYYY-MM-DD from `<input type="date">` as a local
// calendar date (constructor form `new Date(y, m, d)` builds a
// LOCAL date — never UTC-shifted). Returns null on malformed
// input.
function parseYMD(ymd) {
    if (typeof ymd !== "string") return null;
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd);
    if (!m) return null;
    const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    if (Number.isNaN(d.getTime())) return null;
    return d;
}


export default function FirstScheduleSetupSheet({
    open, onOpenChange, submitting, onSubmit,
}) {
    // `selected` holds the accepted Monday as a local Date; `raw`
    // is the last text value entered into the <input type="date">
    // (may be a non-Monday awaiting rejection feedback).
    const [selected, setSelected] = useState(null);
    const [raw, setRaw] = useState("");
    const [showMondayHint, setShowMondayHint] = useState(false);

    // §18 — reopening after Cancel returns to a clean unsubmitted
    // state; no selection carries over between openings.
    const handleOpenChange = (next) => {
        if (!next) {
            setSelected(null);
            setRaw("");
            setShowMondayHint(false);
        }
        onOpenChange?.(next);
    };

    // §8 — non-Monday inputs are NOT accepted. No auto-snap. No
    // silent conversion. Manager sees a Monday-only helper.
    const handleDateChange = (ev) => {
        const value = ev?.target?.value || "";
        setRaw(value);
        const parsed = parseYMD(value);
        if (parsed && parsed.getDay() === 1) {
            setSelected(parsed);
            setShowMondayHint(false);
        } else {
            setSelected(null);
            setShowMondayHint(Boolean(value));
        }
    };

    const ymd = selected ? localYMD(selected) : "";
    const canSubmit = Boolean(ymd) && !submitting;

    const handleCreate = () => {
        if (!canSubmit) return;
        onSubmit?.(ymd);
    };

    return (
        <Sheet open={!!open} onOpenChange={handleOpenChange}>
            <SheetContent
                side="right"
                data-testid="workforce-first-setup-sheet"
                className="sm:max-w-md"
            >
                <SheetHeader>
                    <SheetTitle data-testid="workforce-first-setup-title">
                        {i18n.t("wf.firstScheduleSetupSheet.setUpWorkforceSchedule", "Set up Workforce schedule")}
                    </SheetTitle>
                </SheetHeader>

                <div className="mt-6 space-y-4">
                    <div>
                        <label
                            className="block text-sm font-medium
                                       text-neutral-800"
                            data-testid="workforce-first-setup-field-label"
                            htmlFor="workforce-first-setup-date-input"
                        >
                            {i18n.t("wf.firstScheduleSetupSheet.firstScheduleStarts", "First schedule starts")}
                        </label>
                        <p
                            className="mt-1 text-xs text-neutral-500"
                            data-testid="workforce-first-setup-helper"
                        >
                            {i18n.t("wf.firstScheduleSetupSheet.chooseTheMondayYourFirst3", "Choose the Monday your first 3-week schedule starts.")}
                        </p>
                    </div>

                    <Input
                        id="workforce-first-setup-date-input"
                        type="date"
                        value={raw}
                        onChange={handleDateChange}
                        data-testid="workforce-first-setup-date"
                        aria-invalid={showMondayHint || undefined}
                    />

                    {showMondayHint && (
                        <p
                            className="text-xs text-red-600"
                            data-testid="workforce-first-setup-monday-hint"
                        >
                            {i18n.t("wf.firstScheduleSetupSheet.chooseAMondayForTheFirst", "Choose a Monday for the first schedule.")}
                        </p>
                    )}

                    <div className="flex justify-end gap-2 pt-2">
                        <Button
                            variant="outline"
                            data-testid="workforce-first-setup-cancel"
                            onClick={() => handleOpenChange(false)}
                            disabled={submitting}
                        >
                            {i18n.t("common.cancel", "Cancel")}
                        </Button>
                        <Button
                            onClick={handleCreate}
                            disabled={!canSubmit}
                            data-testid="workforce-first-setup-submit"
                        >
                            {submitting ? i18n.t("wf.firstScheduleSetupSheet.creating", "Creating…") : i18n.t("wf.firstScheduleSetupSheet.createSchedule", "Create schedule")}
                        </Button>
                    </div>
                </div>
            </SheetContent>
        </Sheet>
    );
}
